import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Add to cart without leaving the page, for any tmc section: put data-tmc-cart on a product <form>
 * ({% form 'product' %}). The add goes through Shopify's cart API and announces itself with Horizon's own
 * CartLinesUpdateEvent, so the cart drawer opens and the cart count updates exactly as for Horizon's
 * buttons. Without JS (or if this fails to load) the form simply submits and Shopify opens the cart page.
 *
 * The submit button shows "Adding…" then "Added" (data-label-adding / data-label-added, optional).
 */

const ADDED_MS = 1800;

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-tmc-cart')) return;
  event.preventDefault();

  const button = /** @type {HTMLButtonElement | null} */ (event.submitter ?? form.querySelector('[type="submit"]'));
  if (button?.getAttribute('aria-busy') === 'true') return;
  const label = button?.textContent ?? '';
  setButton(button, 'busy', label);

  const formData = new FormData(form);
  if (!formData.get('quantity')) formData.set('quantity', '1');
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const variantId = String(formData.get('id') ?? '');
  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const config = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, { ...config, headers: { ...config.headers, Accept: 'text/html' } });
    const result = await response.json();
    const cart = await currentCart();
    const detail = {
      items: cart.items,
      source: 'tmc-cart',
      sourceId: form.id,
      itemCount: quantity,
      productId: form.dataset.productId,
      sections: result.sections,
      didError: Boolean(result.status),
    };
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });
    if (result.status) {
      setButton(button, 'error', label, result.description || result.message);
      return;
    }
    setButton(button, 'added', label);
  } catch (error) {
    deferred.reject(error);
    // Fall back to a plain submit so the visitor still gets the product into the cart.
    form.removeAttribute('data-tmc-cart');
    form.requestSubmit(button ?? undefined);
  }
}

/** @returns {Promise<any>} the cart as Shopify's AJAX API returns it */
async function currentCart() {
  /** @type {any} */
  const items = document.querySelector('cart-items-component');
  if (items) {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return response.json();
}

/**
 * @param {HTMLButtonElement | null} button
 * @param {'busy' | 'added' | 'error'} state
 * @param {string} label - the button's normal text
 * @param {string} [message] - error text from Shopify
 */
function setButton(button, state, label, message) {
  if (!button) return;
  if (state === 'busy') {
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.labelAdding ?? 'Adding…';
    return;
  }
  button.removeAttribute('aria-busy');
  button.textContent = state === 'added' ? button.dataset.labelAdded ?? 'Added' : message ?? 'Try again';
  button.dataset.state = state;
  setTimeout(() => {
    button.textContent = label;
    delete button.dataset.state;
  }, state === 'added' ? ADDED_MS : ADDED_MS * 2);
}

if (!(/** @type {any} */ (window).__tmcCart)) {
  /** @type {any} */ (window).__tmcCart = true;
  document.addEventListener('submit', (event) => onSubmit(/** @type {SubmitEvent} */ (event)));
}
